import { defineStore } from "pinia";
import { computed, ref } from "vue";
import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/nano.min.css";
import { useBrandStore } from "@/stores/brand";
import axios from "axios";
import { isUrl } from "@/constants/stringUtils";
import IconCheck from "@/assets/icons/IconCheck.vue";
import { DateTime } from "luxon";

const defaultTextColor = "#FFFFFF";
const colorPickersInDom = [];

export const useStampCardStore = defineStore("stampCard", () => {
  const stpc = ref({
    specification: "dynamic",
    active: true,
    modifier: 1,
    treshold: 50,
  });
  const brandLogo = ref("");
  const editSection = ref("none");

  // Stamp card properties
  const id = ref();
  const description = ref("BUY 8 GET 1 FREE");
  const textColor = ref(defaultTextColor);
  const bgOne = ref("#000000");
  const bgTwo = ref("#FF0000");
  const isGradient = ref(false);
  const angle = ref("90°");
  const gradientType = ref("linear");
  const beforeStampBackgroundColor = ref("#FFFFFF");
  const totalStamps = ref(8);
  const footer = ref("EARN STAMPS WHEN YOU SPEND AT OUR STORE");
  const stampImageUrl = ref("");
  const stampText = ref("");
  const version = ref("");

  // front card properties
  const frontUseLogo = ref(true);
  const frontImageUrl = ref("");
  const frontBrandText = computed(() => {
    return useBrandStore().brand?.name || "";
  });
  const frontLogoText = computed(() => {
    return frontBrandText.value.charAt(0) || "";
  });

  const backgroundColor = computed(() => {
    if (isGradient.value) {
      if (gradientType.value === "linear") {
        return `linear-gradient(${replaceSymbol(angle.value)}deg, ${
          bgOne.value
        }, ${bgTwo.value})`;
      } else {
        return `radial-gradient(circle, ${bgOne.value}, ${bgTwo.value})`;
      }
    } else {
      return bgOne.value;
    }
  });

  const latestCardConfig = ref(null);

  const cardSetting = (config) => {
    if (!config) return {};

    const configureStampImage = () => {
      const { iconImg, iconUrl } = config;

      if (iconImg && !isUrl(iconImg)) {
        return "";
      } else if (iconImg && isUrl(iconImg)) {
        return iconImg;
      } else {
        return iconUrl;
      }
    };

    const configureStampIcon = () => {
      const { iconImg } = config;
      if (iconImg && !isUrl(iconImg)) {
        return iconImg;
      } else {
        return IconCheck;
      }
    };

    const configureRewardDisplay = () => {
      const { rewardIconImg, rewardImageUrl } = config;

      if (rewardImageUrl || (rewardIconImg && isUrl(rewardIconImg))) {
        return {
          rewardEmoji: "",
          rewardImageUrl: rewardImageUrl || rewardIconImg,
        };
      }

      return {
        rewardEmoji: rewardIconImg || "",
        rewardImageUrl: "",
      };
    };

    return {
      description: config.description,
      backgroundColor: config.cardBackgroundColor,
      textColor: config.textColor,
      stampIcon: configureStampIcon(),
      stampImageUrl: configureStampImage(),
      beforeStampsBackgroundColor: config.stampBackgroundColor,
      reward: config.promotion.title,
      footer: config.footer,
      dateCollected: [
        DateTime.now().toJSDate(),
        DateTime.now().plus({ days: 1 }).toJSDate(),
      ],
      totalStamps: config.stampsToComplete,
      version: config.version,
      checkedStamps: 2,
      rewardText: config.promotion.title.slice(0, 10),
      rewardImageUrl: configureRewardDisplay().rewardImageUrl,
      rewardEmoji: configureRewardDisplay().rewardEmoji,
      rewardBackgroundColor: config.rewardBackgroundColor,
      rewardTextColor: config.rewardTextColor,
    };
  };

  // History page properties
  const collections = ref([]);
  const overallCollections = ref(0);

  // Toast properties
  const toast = ref({
    header: "",
    body: "",
    success: false,
  });

  function setColorProperties(colorString) {
    if (typeof colorString !== "string") return;

    const hasGradient = colorString.includes("gradient");

    if (!hasGradient) {
      bgOne.value = colorString;
      return;
    }

    const gradType = colorString.includes("radial") ? "radial" : "linear";
    const angleVal = colorString.match(/(\d+)deg/)?.[1] || "90";

    const colors = colorString.match(/#([0-9a-fA-F]{6})/g);
    const color1 = colors?.[0] || "#000000";
    const color2 = colors?.[1] || "#FF0000";

    gradientType.value = gradType;
    angle.value = angleVal + "°";
    bgOne.value = color1;
    bgTwo.value = color2;
  }

  function reset() {
    id.value = null;
    description.value = "BUY 8 GET 1 FREE";
    textColor.value = defaultTextColor;
    bgOne.value = "#000000";
    bgTwo.value = "#FF0000";
    isGradient.value = false;
    angle.value = "90°";
    gradientType.value = "linear";
    beforeStampBackgroundColor.value = "#FFFFFF";
    totalStamps.value = 8;
    footer.value = "EARN STAMPS WHEN YOU SPEND AT OUR STORE";
    frontImageUrl.value = "";
    stampImageUrl.value = "";
    stampText.value = "";
    frontUseLogo.value = true;
    version.value = "";
    stpc.value = {
      specification: "dynamic",
      active: true,
      modifier: 1,
      treshold: 50,
    };
    brandLogo.value = "";
    collections.value = [];
    overallCollections.value = 0;
    toast.value = {
      header: "",
      body: "",
      success: false,
    };
    editSection.value = "none";
  }

  return {
    id,
    description,
    textColor,
    bgOne,
    bgTwo,
    isGradient,
    angle,
    gradientType,
    beforeStampBackgroundColor,
    footer,
    backgroundColor,
    totalStamps,
    frontImageUrl,
    stampImageUrl,
    stampText,
    frontLogoText,
    frontBrandText,
    stpc,
    brandLogo,
    collections,
    overallCollections,
    version,
    setColorProperties,
    toast,
    frontUseLogo,
    reset,
    cardSetting,
    latestCardConfig,
    editSection,
  };
});

const initPicker = (
  picker,
  defaultColor = null,
  colorPalette = [],
  position,
  modalMode
) => {
  if (colorPalette.length === 0) {
    colorPalette = [defaultTextColor];
  }

  const pickr = Pickr.create({
    el: picker,
    theme: "nano",
    container: modalMode ? picker.parentElement : "body",

    swatches: colorPalette,
    default: defaultColor,
    position: position,

    components: {
      preview: true,
      opacity: true,
      hue: true,

      interaction: {
        input: true,
      },
    },
  });

  pickr.on("hide", (instance) => {
    picker.value = instance._color?.toHEXA().toString();
    picker.dispatchEvent(new Event("input"));
    pickr.applyColor(picker.value);
  });

  colorPickersInDom.push(pickr);
};

export const vInitPicker = {
  mounted: (el, binding) => {
    const pickerPosition = binding?.value || "bottom-middle";

    const modalMode = binding.modifiers.inModal ? true : false;

    initPicker(el, binding.arg, [defaultTextColor], pickerPosition, modalMode);
  },
};

export const replaceSymbol = (value) => {
  if (value) {
    if (typeof value === "number") {
      value = String(value);
    }
    // Remove all '°'
    return value.replace(/°/, "");
  }

  return value;
};

export const uploadImageToServer = (key, file, id, brandId = null) => {
  return new Promise((resolve) => {
    if (!file) {
      return resolve({ error: "No image found" });
    }
    const formData = new FormData();

    formData.append(key, file.includes("http") ? file : dataURLToBlob(file));
    formData.append("stampConfigId", id);
    if (brandId) formData.append("brandId", brandId);

    axios({
      method: "POST",
      url: brandId
        ? `${process.env.VUE_APP_ENDPOINT}simpleloyalty/upload_stamp_card_for_brand`
        : `${process.env.VUE_APP_ENDPOINT}simpleloyalty/upload_stamp_card`,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      withCredentials: true,
    })
      .then((res) => {
        if (res.data && res.data.error) {
          resolve({ error: res.data.error });
        } else {
          resolve({ error: null });
        }
      })
      .catch((err) => {
        resolve({ error: err });
      });
  });
};

export const dataURLToBlob = (dataURL) => {
  const byteString = atob(dataURL.split(",")[1]);
  const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const intArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    intArray[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeString });
};

export const promotionType = {
  percentage_discount: "Percentage Discount",
  free_shipping: "Free Shipping",
  free_item: "Free Item",
  cash_discount: "Cash Discount",
};
